import React, { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { timerAPI } from '../lib/api';
import { formatTimer } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Play, Square, Search, Bell, Timer, Menu, X } from 'lucide-react';
import { toast } from 'sonner';
import GlobalSearchModal from './GlobalSearchModal';
import { useTimerContext } from '@/context/TimerContext';
import NotificationPanel from './NotificationPanel';
import { useNotifications } from '../context/NotificationContext';

export const Header = ({ title, subtitle, isMobileMenuOpen, onToggleMobileMenu }) => {
    const { currentWorkspace, refreshTasks } = useWorkspace();
    const {activeTimer, setActiveTimer, checkActiveTimer} = useTimerContext();
    const { unreadNotifCount } = useNotifications();
    const [timerSeconds, setTimerSeconds] = useState(0);
    const [searchOpen, setSearchOpen] = useState(false);

    useEffect(() => {
        checkActiveTimer();
        setTimerSeconds(0);
    }, []);

    // Timer counting effect
    useEffect(() => {
        let interval;
        if (activeTimer?.is_running) {
            // Calculate initial seconds
            const start = new Date(activeTimer.start_time);
            const now = new Date();
            const initialSeconds = Math.floor((now - start) / 1000);
            setTimerSeconds(initialSeconds <= 0 ? 0 : initialSeconds);

            interval = setInterval(() => {
                setTimerSeconds(prev => prev + 1);
            }, 1000);
        } else {
            setTimerSeconds(0);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [activeTimer]);

    const stopTimer = async () => {
        try {
            await timerAPI.stop();
            setActiveTimer(null);
            setTimerSeconds(0);
            toast.success('Timer stopped and time logged');
            refreshTasks();
        } catch (error) {
            toast.error('Failed to stop timer');
        }
    };

    return (
        <header className="header" data-testid="header">
            {/* Hamburger menu button - visible only on mobile/tablet */}
            <button
                onClick={onToggleMobileMenu}
                className="lg:hidden flex items-center justify-center w-10 h-10 -ml-2 mr-2 rounded-lg hover:bg-gray-100 transition-colors"
                data-testid="mobile-menu-btn"
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
                {isMobileMenuOpen ? (
                    <X className="w-5 h-5 text-brand-text-primary" strokeWidth={1.5} />
                ) : (
                    <Menu className="w-5 h-5 text-brand-text-primary" strokeWidth={1.5} />
                )}
            </button>

            <div className="flex-1">
                <h1 className="font-heading text-xl font-semibold text-brand-text-primary">
                    {title || currentWorkspace?.name || 'Dashboard'}
                </h1>
                {subtitle && (
                    <p className="text-sm text-brand-text-secondary">{subtitle}</p>
                )}
            </div>

            {/* Global Search trigger */}
            <div className="relative w-64 mr-4 hidden sm:block">
                <button
                    type="button"
                    onClick={() => setSearchOpen(true)}
                    onFocus={() => setSearchOpen(true)}
                    className="w-full h-9 pl-9 pr-3 flex items-center bg-brand-background border border-brand-border rounded-md text-sm text-brand-text-muted hover:bg-brand-accent-soft/50 transition-colors"
                    data-testid="search-input"
                >
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" strokeWidth={1.5} />
                    Search anything…
                </button>
            </div>
            <GlobalSearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />

            {/* Active Timer */}
            {activeTimer && (
                <div className="flex items-center gap-3 mr-4 px-4 py-2 bg-brand-accent-soft rounded-lg border border-brand-primary/20">
                    <Timer className="w-5 h-5 text-brand-primary animate-pulse" strokeWidth={1.5} />
                    <span className="timer-display timer-running font-mono text-lg font-semibold" data-testid="active-timer">
                        {formatTimer(timerSeconds)}
                    </span>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={stopTimer}
                        className="text-brand-error hover:bg-brand-error/10 h-8 px-2"
                        data-testid="stop-timer-btn"
                    >
                        <Square className="w-4 h-4" fill="currentColor" strokeWidth={1.5} />
                    </Button>
                </div>
            )}

            {/* Notifications */}
            <NotificationPanel>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                    <Bell className="w-5 h-5 text-brand-text-secondary" strokeWidth={1.5} />
                    {unreadNotifCount > 0 && (
                        <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                            {unreadNotifCount > 9 ? '9+' : unreadNotifCount}
                        </span>
                    )}
                </Button>
            </NotificationPanel>
        </header>
    );
};

export default Header;